import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Wallet, Check, ExternalLink, Loader2, AlertCircle } from 'lucide-react';

/**
 * WalletConnectModal
 * - Connects real Phantom browser extension (not mock)
 * - MetaMask hidden (EVM not used for Solana bot trading)
 * - required=true: modal cannot be dismissed until Phantom is connected
 * - onConnected(address): called when connection succeeds
 */
export default function WalletConnectModal({ open, onClose, onConnected, required = false }) {
  const { wallets, connectWallet, disconnectWallet } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const phantomInstalled = typeof window !== 'undefined' && !!window.solana?.isPhantom;

  // ── Connect real Phantom wallet ──────────────────────────────────────────
  const handleConnectPhantom = async () => {
    setError('');
    setLoading(true);
    try {
      if (!window.solana?.isPhantom) {
        setError('Phantom wallet extension not found. Please install it first.');
        setLoading(false);
        return;
      }

      // Trigger Phantom browser extension popup
      const response = await window.solana.connect();
      const address = response.publicKey.toString();

      // Store in sessionStorage — clears automatically when tab closes
      sessionStorage.setItem('phantom_connected', address);

      // Save to backend (upsert per chain — no duplicates)
      const result = await connectWallet('solana', address, 'phantom');
      if (!result.success) {
        setError(result.error || 'Failed to save wallet connection.');
        setLoading(false);
        return;
      }

      if (onConnected) onConnected(address);
      if (!required) onClose();

    } catch (err) {
      if (err.code === 4001) {
        setError('Wallet connection was rejected. Please try again.');
      } else if (err.message?.includes('locked')) {
        setError('Phantom wallet is locked. Please unlock it and try again.');
      } else {
        setError('Failed to connect wallet. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Disconnect ───────────────────────────────────────────────────────────
  const handleDisconnect = async () => {
    setLoading(true);
    await disconnectWallet('solana');
    sessionStorage.removeItem('phantom_connected');
    setLoading(false);
  };

  // If required, don't allow closing via the X button
  const handleOpenChange = (isOpen) => {
    if (required) return; // lock the modal
    if (!isOpen) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="glass-modal sm:max-w-md"
        // Hide close button when required
        {...(required ? { onPointerDownOutside: (e) => e.preventDefault(), onEscapeKeyDown: (e) => e.preventDefault() } : {})}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Wallet className="w-5 h-5 text-[#E8520F]" />
            Connect Phantom Wallet
          </DialogTitle>
          <DialogDescription className="text-[hsl(215,16%,56%)]">
            {required
              ? 'A Phantom wallet must be connected before accessing the dashboard.'
              : 'Connect your Phantom wallet for Solana trading operations.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">

          {/* Phantom (Solana) — the only wallet for trading */}
          <div className="p-4 rounded-lg border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#AB9FF2] to-[#6366F1] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">P</span>
                </div>
                <div>
                  <p className="font-medium text-white">Phantom</p>
                  <p className="text-xs text-[hsl(215,16%,56%)]">Solana — Trading Wallet</p>
                </div>
              </div>
              {wallets.solana && !required && (
                <div className="flex items-center gap-1 text-[#10B981]">
                  <Check className="w-4 h-4" />
                  <span className="text-xs">Connected</span>
                </div>
              )}
            </div>

            {wallets.solana && !required ? (
              <div className="space-y-2">
                <div className="p-2 bg-[hsl(220,16%,14%)] rounded font-mono text-xs text-[hsl(215,16%,56%)] break-all">
                  {wallets.solana.address}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={loading}
                  className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disconnect'}
                </Button>
              </div>
            ) : phantomInstalled ? (
              <Button
                onClick={handleConnectPhantom}
                disabled={loading}
                className="w-full"
                style={{ background: '#E8520F', boxShadow: '0 0 16px #E8520F30' }}
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Connecting…</>
                  : <><Wallet className="w-4 h-4 mr-2" /> Connect Phantom</>
                }
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-[hsl(215,16%,56%)]">
                  Phantom wallet extension not detected.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://phantom.app/', '_blank')}
                  className="w-full border-[#AB9FF2]/30 text-[#AB9FF2] hover:bg-[#AB9FF2]/10"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Install Phantom
                </Button>
              </div>
            )}
          </div>

          {/* Error display */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs font-mono text-red-400">{error}</p>
            </div>
          )}

          <p className="text-xs text-center text-[hsl(215,16%,45%)]">
            Your wallet signs blockchain transactions. Only Solana wallets are supported for bot trading.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
